/**
 * Phase 4b PR 4 — canonical coach metadata for the React chat surface.
 *
 * Source of truth: api/constants/coaches.js (backend). Mirrored here in
 * TypeScript for type-safe component props. The mapping must stay in
 * sync with the backend — gotcha #4 (coach taxonomy mismatch). The
 * five-coach system below is the only canonical taxonomy in the
 * codebase; do NOT use body/fuel/race/mental/strength.
 *
 * AI-letter naming is from Phase 2/3 onboarding/landing copy. Backend
 * IDs (training-coach, gear-advisor, etc.) match api/constants/coaches.js.
 *
 * Color tokens come from src/app/src/styles/tokens.css; the hex values
 * are duplicated here only as fallbacks (the components prefer the CSS
 * variable). Synchronization: the artifact tokens.css is the source of
 * truth (gotcha #3); the runtime tokens.css mirrors it; this constant
 * mirrors the runtime.
 */

export type CoachId =
  | 'training-coach'
  | 'gear-advisor'
  | 'race-day-strategist'
  | 'recovery-nutrition'
  | 'mental-skills-coach';

export interface Coach {
  id: CoachId;
  /** AI-letter-prefixed display name (Airo / Gair / Vaira / Onai / Kairu). */
  name: string;
  /** Single-word role label, used in the chat header pill and empty state. */
  role: string;
  /** Slightly longer description for the empty state tagline. */
  tagline: string;
  /** First letter of `name` — used in the rounded-square avatar. */
  initial: string;
  /** Coach identity color. Hex (kept for inline-style fallbacks). */
  colorHex: string;
  /** CSS custom-property name. Components prefer this. */
  colorVar: string;
}

export const COACHES: readonly Coach[] = [
  {
    id: 'training-coach',
    name: 'Airo',
    role: 'Training Coach',
    tagline: 'Weekly plans, paces, periodization. Ask anything about your training week.',
    initial: 'A',
    colorHex: '#046F6F',
    colorVar: 'var(--c-training)',
  },
  {
    id: 'gear-advisor',
    name: 'Gair',
    role: 'Gear Advisor',
    tagline: 'Shoes, kit, tech. Specific recommendations grounded in evidence.',
    initial: 'G',
    colorHex: '#8B6B1F',
    colorVar: 'var(--c-gear)',
  },
  {
    id: 'race-day-strategist',
    name: 'Vaira',
    role: 'Race Coach',
    tagline: 'Race-week prep, taper, pacing strategy, day-of routines.',
    initial: 'V',
    colorHex: '#1E40AF',
    colorVar: 'var(--c-race)',
  },
  {
    id: 'recovery-nutrition',
    name: 'Onai',
    role: 'Recovery & Nutrition',
    tagline: 'Fueling, hydration, sleep, recovery between sessions.',
    initial: 'O',
    colorHex: '#0F7A5A',
    colorVar: 'var(--c-recovery)',
  },
  {
    id: 'mental-skills-coach',
    name: 'Kairu',
    role: 'Mindset Coach',
    tagline: 'Confidence, focus, race-day nerves, mental skills training.',
    initial: 'K',
    colorHex: '#5E3A9E',
    colorVar: 'var(--c-mental)',
  },
] as const;

const COACH_BY_ID = new Map<CoachId, Coach>(COACHES.map((c) => [c.id, c]));

/** Look up a coach by ID. Returns undefined for unknown IDs. */
export function coachById(id: string): Coach | undefined {
  return COACH_BY_ID.get(id as CoachId);
}

/** Default coach when the user lands on /v2/chat with no prior selection. */
export const DEFAULT_COACH_ID: CoachId = 'training-coach';

/** Set of valid coach IDs — useful for runtime validation. */
export const COACH_IDS: readonly CoachId[] = COACHES.map((c) => c.id);

/** Type guard. */
export function isCoachId(value: unknown): value is CoachId {
  return typeof value === 'string' && COACH_IDS.includes(value as CoachId);
}
